import { api, ensureApiAuthenticated } from '../services/api';

export interface PullListParams {
  top_n?: number;
  min_price?: number;
}

export interface ReconciliationReportParams {
  view?: 'daily' | 'weekly';
  channel?: string;
  set_name?: string;
  condition?: string;
  language?: string;
  discrepancy_type?: 'missing_listing' | 'quantity_mismatch' | 'price_mismatch' | 'unknown';
  limit?: number;
}

export interface ChannelSyncReliabilityReportParams {
  days?: number;
  channel?: string;
}

export interface OutboxDeliveryReliabilityReportParams {
  view?: 'daily' | 'weekly';
  days?: number;
  channel?: string;
}

export interface FanoutSloBreachReportParams {
  days?: number;
}

export interface PullListItem {
  rank: number;
  name: string;
  set_name: string | null;
  card_number: string | null;
  condition: string | null;
  printing: string | null;
  is_foil: boolean;
  language: string | null;
  preferred_price: number | null;
  market_price: number | null;
  market_source: string | null;
  copy_count: number;
  edh_rank: number | null;
  locations: string[];
}

export interface PullListResponse {
  generated_at: string;
  params: {
    top_n: number | null;
    min_price: number | null;
    statuses: string[];
    format: 'json';
  };
  total_cards: number;
  items: PullListItem[];
}

export interface ReconciliationSummary {
  total_runs: number;
  total_discrepancies: number;
  discrepancy_counts: Record<string, number>;
  raw_discrepancy_counts: Record<string, number>;
  policy_outcomes: Record<string, number>;
  unresolved_variants: number;
  resolved_variants: number;
}

export interface ReconciliationTrendPoint {
  period_start: string;
  period_end: string;
  total_discrepancies: number;
  discrepancy_counts: Record<string, number>;
  policy_outcomes: Record<string, number>;
  channels: Record<string, number>;
}

export interface ReconciliationTopRecurringSku {
  channel: string;
  product_id: string | null;
  product_name: string;
  set_name: string | null;
  sku: string | null;
  condition: string | null;
  language: string | null;
  printing: string | null;
  foil: boolean;
  discrepancy_type: string;
  raw_discrepancy_classes: Record<string, number>;
  occurrences: number;
  last_seen_at: string | null;
  current_policy_action: string | null;
  unresolved: boolean;
  local_qty: number;
  remote_qty: number;
  quantity_delta: number;
  recommended_action: string | null;
}

export interface ReconciliationAgeBucket {
  bucket: string;
  count: number;
}

export interface ReconciliationReportResponse {
  generated_at: string;
  params: {
    view: 'daily' | 'weekly';
    channel: string | null;
    set_name: string | null;
    condition: string | null;
    language: string | null;
    discrepancy_type: string | null;
    limit: number;
    lookback_days: number;
    format: 'json';
  };
  summary: ReconciliationSummary;
  trend: ReconciliationTrendPoint[];
  top_recurring_skus: ReconciliationTopRecurringSku[];
  unresolved_age_buckets: ReconciliationAgeBucket[];
}

export interface ChannelSyncReliabilityChannelSummary {
  channel: string;
  success_count: number;
  failed_count: number;
  other_count: number;
  failure_rate_pct: number;
  p50_duration_seconds: number | null;
  p95_duration_seconds: number | null;
  last_successful_sync_at: string | null;
}

export interface ChannelSyncReliabilityTopErrorClass {
  error_class: string;
  count: number;
  first_seen_at: string | null;
  last_seen_at: string | null;
}

export interface ChannelSyncReliabilityDailyTrendPoint {
  date: string;
  channel: string;
  success_count: number;
  failed_count: number;
  other_count: number;
  failure_rate_pct: number;
}

export interface ChannelSyncReliabilityReportResponse {
  generated_at: string;
  params: {
    days: number;
    channel: string | null;
    format: 'json';
  };
  summary: {
    total_success_count: number;
    total_failed_count: number;
    channels: ChannelSyncReliabilityChannelSummary[];
  };
  comparison: {
    shopify: ChannelSyncReliabilityChannelSummary | null;
    manapool: ChannelSyncReliabilityChannelSummary | null;
  };
  top_error_classes: ChannelSyncReliabilityTopErrorClass[];
  daily_trend: ChannelSyncReliabilityDailyTrendPoint[];
}

export interface OutboxDeliveryRollup {
  period_start: string;
  period_end: string;
  channel: string;
  queued_volume: number;
  pending_count: number;
  oldest_pending_age_seconds: number;
  dispatch_success: number;
  dispatch_failed: number;
  dispatch_failure_rate_pct: number;
  retry_total: number;
  retry_exhaustions: number;
  lag_p50_seconds: number | null;
  lag_p95_seconds: number | null;
}

export interface OutboxReplayOperation {
  replay_key: string;
  channels: string[];
  is_dry_run: boolean;
  queue_entries: number;
  completed: number;
  failed: number;
  pending: number;
  first_seen_at: string | null;
  last_seen_at: string | null;
}

export interface OutboxDeliveryReliabilityReportResponse {
  generated_at: string;
  params: {
    view: 'daily' | 'weekly';
    days: number;
    channel: string | null;
    format: 'json';
  };
  summary: {
    queued_volume: number;
    pending_count: number;
    dispatch_success: number;
    dispatch_failed: number;
    retry_total: number;
    retry_exhaustions: number;
  };
  rollups: OutboxDeliveryRollup[];
  replay_operations: OutboxReplayOperation[];
  anomaly_indicators: {
    backlog_acceleration: boolean;
    stale_queue_growth: boolean;
  };
}

export interface FanoutSloDailySummary {
  date: string;
  total_breach_windows: number;
  severity_counts: Record<string, number>;
  metric_counts: Record<string, number>;
  counter_deltas: {
    retry_exhaustion_total: number;
    oversell_exception_total: number;
  };
}

export interface FanoutSloBreachWindow {
  metric_name: string;
  severity: string;
  threshold_type: string;
  threshold_value: number;
  breach_start: string;
  breach_end: string;
  recovered_at: string | null;
  duration_seconds: number;
  max_observed_value: number;
  sample_count: number;
}

export interface FanoutSloBreachReportResponse {
  generated_at: string;
  params: {
    days: number;
    format: 'json';
  };
  summary: {
    total_breach_windows: number;
    severity_counts: Record<string, number>;
    metric_counts: Record<string, number>;
  };
  daily_summary: FanoutSloDailySummary[];
  breach_windows: FanoutSloBreachWindow[];
}

export async function fetchPullList(params: PullListParams = {}): Promise<PullListResponse> {
  await ensureApiAuthenticated();
  const response = await api.get<PullListResponse>('/reports/pull-list', { params });
  return response.data;
}

export async function fetchReconciliationReport(
  params: ReconciliationReportParams = {},
): Promise<ReconciliationReportResponse> {
  await ensureApiAuthenticated();
  const response = await api.get<ReconciliationReportResponse>('/reports/reconciliation', { params });
  return response.data;
}

export async function fetchChannelSyncReliabilityReport(
  params: ChannelSyncReliabilityReportParams = {},
): Promise<ChannelSyncReliabilityReportResponse> {
  await ensureApiAuthenticated();
  const response = await api.get<ChannelSyncReliabilityReportResponse>('/reports/channel-sync-reliability', { params });
  return response.data;
}

export async function fetchOutboxDeliveryReliabilityReport(
  params: OutboxDeliveryReliabilityReportParams = {},
): Promise<OutboxDeliveryReliabilityReportResponse> {
  await ensureApiAuthenticated();
  const response = await api.get<OutboxDeliveryReliabilityReportResponse>('/reports/outbox-delivery-reliability', { params });
  return response.data;
}

export async function fetchFanoutSloBreachReport(
  params: FanoutSloBreachReportParams = {},
): Promise<FanoutSloBreachReportResponse> {
  await ensureApiAuthenticated();
  const response = await api.get<FanoutSloBreachReportResponse>('/reports/fanout-slo-breaches', { params });
  return response.data;
}

export interface ChannelParitySnapshotMeta {
  started_at: string;
  finished_at: string;
  page_count: number;
  fetched_count: number;
  is_complete: boolean;
}

export interface ChannelParityDriftRow {
  key: string;
  product_id: string | null;
  product_name: string | null;
  set_name?: string | null;
  condition?: string | null;
  finish?: string | null;
  language?: string | null;
  variant_id?: string | null;
  drift_types: string[];
  expected_quantity: number | null;
  channel_quantity: number | null;
  expected_price: string | null;
  channel_price: string | null;
}

export interface ChannelParityRootDiffPair {
  suffix: string;
  expected_key: string;
  channel_key: string;
  expected_root: string;
  channel_root: string;
}

export interface ChannelParityChannelSuccess {
  status: 'success';
  channel: string;
  source_of_truth: 'syncbase';
  snapshot_meta: ChannelParitySnapshotMeta;
  total_expected_items: number;
  total_channel_items: number;
  syncbase_sellable_copies: number;
  channel_copies: number;
  copy_delta: number;
  manapool_unshipped_allocated_copies?: number;
  recent_manapool_allocated_copies?: number;
  unmatched_keys_count: number;
  in_sync_count: number;
  missing_in_channel: number;
  extra_in_channel: number;
  quantity_mismatches: number;
  price_mismatches: number;
  total_drift_count: number;
  inventory_drift_count?: number;
  identity_drift_count?: number;
  parity_pass: boolean;
  inventory_parity_pass?: boolean;
  identity_parity_pass?: boolean;
  likely_identity_skew?: boolean;
  root_diff_same_suffix_count?: number;
  root_diff_same_suffix_sample?: ChannelParityRootDiffPair[];
  true_missing_estimate?: number;
  true_extra_estimate?: number;
  sample_drift: ChannelParityDriftRow[];
}

export interface ChannelParityChannelError {
  status: 'error';
  channel: string;
  source_of_truth: 'syncbase';
  error_type: string;
  error: string;
  total_expected_items: number;
  unmatched_keys_count: number;
  parity_pass: false;
}

export type ChannelParityChannelResult = ChannelParityChannelSuccess | ChannelParityChannelError;

export interface ChannelParityReportResponse {
  generated_at: string;
  source_of_truth: 'syncbase';
  requested_channels: string[];
  parity_pass: boolean;
  channels: ChannelParityChannelResult[];
}

export interface ChannelParityReportParams {
  channels?: string;
}

export type ChannelParityJobStatus = 'queued' | 'running' | 'success' | 'failed';

export interface ChannelParityJob {
  job_id: string;
  status: ChannelParityJobStatus;
  requested_channels: string[];
  requested_by: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  updated_at: string;
  duration_seconds?: number | null;
  error_message: string | null;
  result: ChannelParityReportResponse | null;
}

export interface ChannelParityJobHistoryResponse {
  jobs: ChannelParityJob[];
}

export interface ChannelParityMonitorResponse {
  generated_at: string;
  stale_after_minutes: number;
  monitor_status: 'healthy' | 'drift' | 'stale' | 'failed' | 'never_run';
  is_stale: boolean;
  last_checked_at: string | null;
  age_minutes: number | null;
  total_drift_count: number;
  latest_job: ChannelParityJob | null;
  manapool_uuid_verification?: {
    dual_finish_products?: number;
    missing_stamp?: number;
    same_uuid_conflict?: number;
    distinct_ok?: number;
    ok?: boolean;
    safe_for_outbound?: boolean;
  } | null;
  scheduler_status: {
    status: string | null;
    last_run_at: string | null;
    details: Record<string, unknown> | null;
  };
}

export interface ChannelParityMonitorParams {
  stale_after_minutes?: number;
}

export type ListingCoverageFreshness = 'current' | 'stale' | 'never_run';

export type ListingCoverageState =
  | 'measured'
  | 'stale'
  | 'never_run'
  | 'missing_from_snapshot'
  | 'error';

export interface ListingCoverageChannel {
  channel: string;
  coverage_state: ListingCoverageState;
  expected_items: number | null;
  listed_items: number | null;
  coverage_pct: number | null;
}

export interface ListingCoverageReportResponse {
  generated_at: string;
  source: 'latest_successful_parity_job';
  live_channel_scrape: boolean;
  stale_after_minutes: number;
  freshness: ListingCoverageFreshness;
  job_id: string | null;
  job_finished_at: string | null;
  snapshot_generated_at: string | null;
  age_minutes: number | null;
  channels: ListingCoverageChannel[];
}

export interface PricingBackfillParams {
  dry_run?: boolean;
  limit?: number;
  scope?: 'overrides' | 'all_active';
}

export interface PricingBackfillResponse {
  total_found: number;
  events_emitted: number;
  targets: string[];
  dry_run: boolean;
  scope: 'overrides' | 'all_active';
  message?: string;
}

export interface QuantityBackfillParams {
  dry_run?: boolean;
  limit?: number;
  scope?: 'sellable_only' | 'all_active';
}

export interface QuantityBackfillResponse {
  total_found: number;
  events_emitted: number;
  targets: string[];
  dry_run: boolean;
  scope: 'sellable_only' | 'all_active';
  message?: string;
  skipped_shipped_shortfall_count?: number;
}

export interface ParityQuantityRemediationParams {
  channel?: 'shopify' | 'manapool';
  dry_run?: boolean;
  limit?: number;
  /** When true, rebuild live parity instead of reusing the last successful job. */
  force_refresh?: boolean;
}

export interface ParityQuantityRemediationResponse {
  channel: string;
  dry_run: boolean;
  drift_rows_seen: number;
  variant_ids_targeted: number;
  events_emitted: number;
  targets: string[];
  parity_pass?: boolean;
  total_drift_count?: number;
  /** `parity_job` = reused last successful job; `live` = full channel scrape. */
  parity_source?: 'parity_job' | 'live';
  parity_job_id?: string | null;
  parity_generated_at?: string | null;
  message?: string;
  verification?: {
    dual_finish_products?: number;
    missing_stamp?: number;
    same_uuid_conflict?: number;
    distinct_ok?: number;
    ok?: boolean;
    safe_for_outbound?: boolean;
  } | null;
  skipped_missing_stamp_count?: number;
  sample_skipped_missing_stamp_rows?: Array<{
    key?: string;
    product_id?: string | null;
    product_name?: string | null;
    condition?: string | null;
    finish?: string | null;
    language?: string | null;
    variant_id?: string | null;
  }>;
  skipped_shipped_shortfall_count?: number;
  sample_skipped_shipped_shortfall_rows?: Array<{
    variant_id?: string;
    key?: string;
    order_external_id?: string;
    card_name?: string;
    shortfall_qty?: number;
    in_stock_now?: number;
  }>;
  sample_actionable_rows?: Array<{
    key?: string;
    product_id?: string | null;
    product_name?: string | null;
    condition?: string | null;
    finish?: string | null;
    language?: string | null;
    variant_id?: string | null;
    drift_types?: string[];
    expected_quantity?: number | null;
    channel_quantity?: number | null;
  }>;
}

export interface ShippedShortfallWithStockResponse {
  generated_at: string;
  count: number;
  alert_state: 'ok' | 'critical' | string;
  source_filter?: string | null;
  summary?: {
    line_count: number;
    total_shortfall_qty: number;
    total_in_stock_now: number;
    unique_orders: number;
    unique_variants: number;
  };
  rows: Array<{
    source?: string;
    order_external_id?: string;
    card_name?: string;
    set_name?: string | null;
    card_number?: string | null;
    finish?: string | null;
    shortfall_qty?: number;
    in_stock_now?: number;
    variant_id?: string;
  }>;
}

export async function startChannelParityJob(
  params: ChannelParityReportParams = {},
): Promise<ChannelParityJob> {
  await ensureApiAuthenticated();
  const response = await api.post<ChannelParityJob>('/reports/channel-parity/jobs', undefined, { params });
  return response.data;
}

export async function fetchChannelParityJob(jobId: string): Promise<ChannelParityJob> {
  await ensureApiAuthenticated();
  const response = await api.get<ChannelParityJob>(`/reports/channel-parity/jobs/${jobId}`);
  return response.data;
}

export async function cancelChannelParityJob(jobId: string): Promise<ChannelParityJob> {
  await ensureApiAuthenticated();
  const response = await api.post<ChannelParityJob>(`/reports/channel-parity/jobs/${jobId}/cancel`);
  return response.data;
}

export async function fetchChannelParityJobHistory(limit = 20): Promise<ChannelParityJobHistoryResponse> {
  await ensureApiAuthenticated();
  const response = await api.get<ChannelParityJobHistoryResponse>('/reports/channel-parity/jobs', {
    params: { limit },
  });
  return response.data;
}

export async function fetchChannelParityMonitor(
  params: ChannelParityMonitorParams = {},
): Promise<ChannelParityMonitorResponse> {
  await ensureApiAuthenticated();
  const response = await api.get<ChannelParityMonitorResponse>('/reports/channel-parity/monitor', { params });
  return response.data;
}

export async function fetchListingCoverageReport(): Promise<ListingCoverageReportResponse> {
  await ensureApiAuthenticated();
  const response = await api.get<ListingCoverageReportResponse>('/reports/listing-coverage');
  return response.data;
}

export interface InventoryValueBucket {
  copy_count: number;
  cost: number;
  preferred_value: number;
  preferred_unpriced_count: number;
  market_value: number;
  market_unpriced_count: number;
}

export interface InventoryValueStatusRow extends InventoryValueBucket {
  status: string;
}

export interface InventoryValueListingRow extends InventoryValueBucket {
  listed_on_channel: boolean;
}

export interface InventoryValueReportResponse {
  generated_at: string;
  status_filter: string[];
  excluded_statuses: string[];
  status_filter_note: string;
  etched_price_note: string;
  channel_listing_note: string;
  price_fields: {
    cost: string;
    preferred: string[];
    market: string[];
  };
  totals: InventoryValueBucket;
  by_status: InventoryValueStatusRow[];
  by_channel_listing: InventoryValueListingRow[];
}

export async function fetchInventoryValueReport(): Promise<InventoryValueReportResponse> {
  await ensureApiAuthenticated();
  const response = await api.get<InventoryValueReportResponse>('/reports/inventory-value');
  return response.data;
}

export async function downloadInventoryValueCsv(): Promise<void> {
  await ensureApiAuthenticated();
  const response = await api.get<Blob>('/reports/inventory-value', {
    params: { format: 'csv' },
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `inventory-value_${Date.now()}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function triggerPricingBackfill(
  params: PricingBackfillParams = {},
): Promise<PricingBackfillResponse> {
  await ensureApiAuthenticated();
  const response = await api.post<PricingBackfillResponse>('/admin/pricing/backfill-outbox', undefined, {
    params,
  });
  return response.data;
}

export async function triggerQuantityBackfill(
  params: QuantityBackfillParams = {},
): Promise<QuantityBackfillResponse> {
  await ensureApiAuthenticated();
  const response = await api.post<QuantityBackfillResponse>('/admin/quantity/backfill-outbox', undefined, {
    params,
  });
  return response.data;
}

export async function remediateParityQuantityDrift(
  params: ParityQuantityRemediationParams = {},
): Promise<ParityQuantityRemediationResponse> {
  await ensureApiAuthenticated();
  const response = await api.post<ParityQuantityRemediationResponse>(
    '/reports/channel-parity/remediate-quantity',
    undefined,
    {
      params,
      // Shopify live scrape can exceed 3m; reused snapshots are fast.
      timeout: 5 * 60 * 1000,
    },
  );
  return response.data;
}

export interface FulfillmentAgingRow {
  order_id: string;
  source: string;
  external_id: string;
  fulfillment_status: string;
  paid_at: string | null;
  age_seconds: number | null;
  buyer_name: string | null;
  shipping_method: string | null;
  tracking_number: string | null;
}

export interface FulfillmentAgingResponse {
  generated_at: string;
  count: number;
  source_filter?: string | null;
  counts: {
    ready: number;
    on_hold: number;
    fulfillment_queued: number;
    label_created: number;
    error_retry: number;
  };
  rows: FulfillmentAgingRow[];
}

export async function fetchFulfillmentAging(
  params: { limit?: number; source?: string } = {},
): Promise<FulfillmentAgingResponse> {
  await ensureApiAuthenticated();
  const response = await api.get<FulfillmentAgingResponse>('/reports/fulfillment-aging', {
    params,
  });
  return response.data;
}

export async function fetchShippedShortfallWithStock(
  params: { limit?: number; source?: string } = {},
): Promise<ShippedShortfallWithStockResponse> {
  await ensureApiAuthenticated();
  const response = await api.get<ShippedShortfallWithStockResponse>(
    '/reports/shipped-shortfall-with-stock',
    { params },
  );
  return response.data;
}

export async function downloadPullListCsv(params: PullListParams = {}): Promise<void> {
  await ensureApiAuthenticated();

  const response = await api.get<Blob>('/reports/pull-list', {
    params: {
      ...params,
      format: 'csv',
    },
    responseType: 'blob',
  });

  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `pull-list_${Date.now()}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadReconciliationReportCsv(
  params: ReconciliationReportParams = {},
): Promise<void> {
  await ensureApiAuthenticated();

  const response = await api.get<Blob>('/reports/reconciliation', {
    params: {
      ...params,
      format: 'csv',
    },
    responseType: 'blob',
  });

  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `inventory-reconciliation_${Date.now()}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadChannelSyncReliabilityReportCsv(
  params: ChannelSyncReliabilityReportParams = {},
): Promise<void> {
  await ensureApiAuthenticated();

  const response = await api.get<Blob>('/reports/channel-sync-reliability', {
    params: {
      ...params,
      format: 'csv',
    },
    responseType: 'blob',
  });

  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `channel-sync-reliability_${Date.now()}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadOutboxDeliveryReliabilityReportCsv(
  params: OutboxDeliveryReliabilityReportParams = {},
): Promise<void> {
  await ensureApiAuthenticated();

  const response = await api.get<Blob>('/reports/outbox-delivery-reliability', {
    params: {
      ...params,
      format: 'csv',
    },
    responseType: 'blob',
  });

  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `outbox-delivery-reliability_${Date.now()}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadFanoutSloBreachReportCsv(
  params: FanoutSloBreachReportParams = {},
): Promise<void> {
  await ensureApiAuthenticated();

  const response = await api.get<Blob>('/reports/fanout-slo-breaches', {
    params: {
      ...params,
      format: 'csv',
    },
    responseType: 'blob',
  });

  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `fanout-slo-breaches_${Date.now()}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}


export interface SalesCogsSummary {
  start_date: string;
  end_date: string;
  source?: string | null;
  row_count: number;
  sale_count: number;
  reversal_count: number;
  product_revenue: number;
  cogs: number;
  gross_profit: number;
  fees: number;
  net_profit: number;
  incomplete_cogs_rows: number;
  on_hand_qty?: number;
  on_hand_cost?: number;
  /** Always point-in-time. This snapshot is not a period P&L figure. */
  valuation_basis?: 'point_in_time' | string;
  /** Copy statuses included in on_hand_cost. Parity sellable set plus ck_trade_hold. */
  on_hand_statuses?: string[];
  /** Every copy in ck_trade_hold. Included in on_hand_cost. */
  ck_trade_hold_qty?: number;
  ck_trade_hold_cost?: number;
  /** Cash CK payouts whose paid date falls in the report range. Not product revenue. */
  ck_proceeds?: number;
  /** Pinned acquisition cost of the cash batches in ck_proceeds. */
  ck_cogs?: number;
  ck_batch_count?: number;
  /** Store-credit CK payouts in the report range. Recorded apart from sales revenue. */
  ck_credit_amount?: number;
  ck_credit_cogs?: number;
  ck_credit_batch_count?: number;
  /** Pin cost of ck_trade_hold copies on batches with no paid date. */
  ck_pulled_unpaid_cost?: number;
  ck_pulled_unpaid_qty?: number;
  shrink_events?: number;
  shrink_qty?: number;
}

export interface SalesCogsRow {
  row_type: 'sale' | 'reversal' | string;
  date: string;
  order_id: string;
  external_id?: string | null;
  channel: string;
  product_revenue: number;
  cogs: number;
  gross_profit: number;
  shipping: number;
  taxes: number;
  fees: number;
  fees_source?: string | null;
  order_total: number;
  net_profit: number;
  commercial_status?: string | null;
  cogs_complete: boolean;
  line_id?: string | null;
  release_reason?: string | null;
}

export interface SalesCogsReportResponse {
  summary: SalesCogsSummary;
  page: number;
  page_size: number;
  total_rows: number;
  rows: SalesCogsRow[];
}

export async function fetchSalesCogsReport(params: {
  start_date: string;
  end_date: string;
  source?: string;
  page?: number;
  page_size?: number;
}): Promise<SalesCogsReportResponse> {
  const response = await api.get<SalesCogsReportResponse>('/reports/sales-cogs', { params });
  return response.data;
}

export interface SalesOverviewChannel {
  channel: string;
  revenue: number;
  orders: number;
  fees: number;
  net: number;
}

export interface SalesOverviewPeriod {
  key: string;
  label: string;
  start_date: string;
  end_date: string;
  revenue: number;
  orders: number;
  fees: number;
  net: number;
  channels: SalesOverviewChannel[];
}

export interface SalesOverviewOrder {
  order_id: string;
  external_id?: string | null;
  channel?: string | null;
  date: string;
  ordered_at?: string | null;
  revenue: number;
  fees: number;
  net: number;
  cogs_complete: boolean;
  commercial_status?: string | null;
}

export interface SalesOverviewResponse {
  as_of: string;
  timezone: string;
  periods: SalesOverviewPeriod[];
  recent_orders: SalesOverviewOrder[];
}

export async function fetchSalesOverview(): Promise<SalesOverviewResponse> {
  const response = await api.get<SalesOverviewResponse>('/reports/sales-overview');
  return response.data;
}

export async function downloadSalesCogsCsv(params: {
  start_date: string;
  end_date: string;
  source?: string;
  format?: 'csv' | 'csv_lines';
}): Promise<Blob> {
  const response = await api.get<Blob>('/reports/sales-cogs', {
    params: { ...params, format: params.format || 'csv' },
    responseType: 'blob',
  });
  return response.data;
}
